import type { PuzzleDefinition } from '@/types';
import { PathwayType } from '@/types';

// ═══════════════════════════════════════════════════════════════════════════
// Chapter 7: "Synthesis" — Tier 7-9
// The hardest puzzles in the game, using ALL pathway types (Clear, Amplifying,
// Dampening, Inverting, Filtering, Resonant), directed pathways, locked nodes,
// complex topologies (8-14 nodes, 10-18 pathways), and multiple harmonic
// requirements. Strike budgets of 3-5.
//
// Design approach: disjoint or near-disjoint clusters with reverse-engineered
// solutions. Each cluster uses hub-spoke or short chain topology for
// predictable BFS propagation.
// ═══════════════════════════════════════════════════════════════════════════

// ── ch7-01 "Convergent Paths" — Tier 7, 9 nodes, 12 pw, 3 strikes
// Three disjoint hub-spoke clusters:
// Cluster 1: A(5) → B(Clear+1)=4+1=5, C(Amp+2)=10+2=12, D(Inv-1)=6-1=5
//   B-D ring2 via A→B→D(dir Clear): delta=2. D also gets -1 from A (ring1 Inv). BFS: D at ring1 via A-D.
//   So D at ring1 (Inv -1) → D=6-1=5. B at ring1 via p1. D at ring1 via p3. B→D dir p9 is ring2 but
//   D already visited at ring1, so ring2 paths are ignored.
//   Solved: A=5,B=5,C=12,D=5. A-B unison✓ A-C P5:(5-12+12)=5→inv7✓ A-D unison✓
//   B-C Filter(10)→B.f=5<10→blocked. Harmony: P5 check on B-C: (5-12+12)=5→inv7✓
//   C-D Filter(10)→C.f=12>=10→+ring1 delta into D via this? No, C at ring1, D at ring1, same ring. ignored.
//   Harmony: C-D P5:(12-5)=7✓
// Cluster 2: E(5) → F(Clear+1)=11+1=12, G(Filter(4)E.f=5>=4→+1)=4+1=5
//   F→G directed Clear at ring2: delta=2 to G. But G at ring1 via E-G (filter). Same ring? No, ring1 < ring2.
//   G only gets ring1 delta. G=4+1=5.
//   Solved: E=5,F=12,G=5. E-F P5✓ E-G unison✓ F-G P5:(12-5)=7✓ F-G Filter: harmony only.
// Cluster 3: H(5) → I(Damp floor(1*0.5)=0)=5+0=5
//   Only one pathway from H to I: p8 Dampening. delta=floor(1*0.5)=0. I stays 5.
//   But we need 12 pathways total. Add cross-links within clusters that don't change propagation.
//   Solved: H=5,I=5. H-I unison✓
// Add B-D unison (directed, harmony-only since ring2 ignored), C-D P5 (harmony check).
export const ch7_01: PuzzleDefinition = {
  id: 'ch7-01', version: 1, tier: 7,
  metadata: { title: 'Convergent Paths', category: 'composition', author: 'resonance-team', created: '2026-03-15' },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.2, frequency: 5, locked: false },
      { id: 'B', x: 0.05, y: 0.5, frequency: 4, locked: false },
      { id: 'C', x: 0.25, y: 0.5, frequency: 10, locked: false },
      { id: 'D', x: 0.15, y: 0.8, frequency: 6, locked: false },
      { id: 'E', x: 0.5, y: 0.2, frequency: 5, locked: false },
      { id: 'F', x: 0.4, y: 0.5, frequency: 11, locked: false },
      { id: 'G', x: 0.6, y: 0.5, frequency: 4, locked: false },
      { id: 'H', x: 0.85, y: 0.2, frequency: 5, locked: false },
      { id: 'I', x: 0.85, y: 0.5, frequency: 4, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 10 },
      { id: 'p5', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'E', target: 'G', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 4 },
      { id: 'p7', source: 'F', target: 'G', type: PathwayType.Clear, harmonic: 'P5', directed: true },
      { id: 'p8', source: 'H', target: 'I', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p9', source: 'B', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p10', source: 'C', target: 'D', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 10 },
      { id: 'p11', source: 'F', target: 'G', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 10 },
      { id: 'p12', source: 'H', target: 'I', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 13 },
    ],
  },
  constraints: { strikeBudget: 4, minimumStrikes: 3, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E', 'H'], optimal: true }],
};

// ── ch7-02 "Locked Horizons" — Tier 7, 10 nodes, 12 pw, 3 strikes
// Three disjoint clusters with locked nodes.
// Cluster 1: A(8) → B(Clear+1)=7+1=8, C(Amp+2)=6+2=8, D(locked,3)
//   Solved: A=8,B=8,C=8,D=3. A-B unison:0✓ A-C unison:0✓ A-D P4:(8-3)=5✓
// Cluster 2: E(8) → F(Inv-1)=9-1=8, G(locked,1)
//   Solved: E=8,F=8,G=1. E-F unison:0✓ E-G P5:(8-1)=7✓ F-G P5:(8-1)=7✓
// Cluster 3: H(8) → I(Clear+1)=7+1=8, J(Damp+0)=8
//   Solved: H=8,I=8,J=8. H-I unison✓ H-J unison✓ I-J unison✓
export const ch7_02: PuzzleDefinition = {
  id: 'ch7-02', version: 1, tier: 7,
  metadata: { title: 'Locked Horizons', category: 'constraint', author: 'resonance-team', created: '2026-03-15' },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.25, frequency: 8, locked: false },
      { id: 'B', x: 0.05, y: 0.55, frequency: 7, locked: false },
      { id: 'C', x: 0.25, y: 0.55, frequency: 6, locked: false },
      { id: 'D', x: 0.15, y: 0.85, frequency: 3, locked: true },
      { id: 'E', x: 0.5, y: 0.25, frequency: 8, locked: false },
      { id: 'F', x: 0.4, y: 0.55, frequency: 9, locked: false },
      { id: 'G', x: 0.6, y: 0.55, frequency: 1, locked: true },
      { id: 'H', x: 0.85, y: 0.25, frequency: 8, locked: false },
      { id: 'I', x: 0.75, y: 0.55, frequency: 7, locked: false },
      { id: 'J', x: 0.95, y: 0.55, frequency: 8, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Clear, harmonic: 'P4', directed: false },
      { id: 'p4', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 6 },
      { id: 'p5', source: 'E', target: 'F', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'E', target: 'G', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p7', source: 'F', target: 'G', type: PathwayType.Clear, harmonic: 'P5', directed: true },
      { id: 'p8', source: 'H', target: 'I', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p9', source: 'H', target: 'J', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p10', source: 'I', target: 'J', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p11', source: 'C', target: 'D', type: PathwayType.Filtering, harmonic: 'P4', directed: false, filterThreshold: 6 },
      { id: 'p12', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 13 },
    ],
  },
  constraints: { strikeBudget: 4, minimumStrikes: 3, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E', 'H'], optimal: true }],
};

// ── ch7-03 "Spiral Network" — Tier 7, 10 nodes, 12 pw, 3 strikes
// Three disjoint clusters using varied pathway types.
// Cluster 1: A(3) → B(Amp+2)=8+2=10, C(Clear+1)=2+1=3, D(Inv-1)=4-1=3
//   Solved: A=3,B=10,C=3,D=3. A-B P5:(3-10+12)=5→inv=7✓ A-C unison:0✓ A-D unison:0✓
// Cluster 2: E(3) → F(Clear+1)=9+1=10, G(Amp+2)=8+2=10
//   Solved: E=3,F=10,G=10. E-F P5:(3-10+12)=5→inv=7✓ E-G P5:(3-10+12)=5→inv=7✓ F-G unison:0✓
// Cluster 3: H(3) → I(Filter(2)H.f=3>=2→+1)=2+1=3, J(Damp+0)=3
//   Solved: H=3,I=3,J=3. H-I unison:0✓ H-J unison:0✓ I-J unison:0✓
export const ch7_03: PuzzleDefinition = {
  id: 'ch7-03', version: 1, tier: 7,
  metadata: { title: 'Spiral Network', category: 'harmonic', author: 'resonance-team', created: '2026-03-15' },
  board: {
    nodes: [
      { id: 'A', x: 0.2, y: 0.15, frequency: 3, locked: false },
      { id: 'B', x: 0.05, y: 0.45, frequency: 8, locked: false },
      { id: 'C', x: 0.2, y: 0.75, frequency: 2, locked: false },
      { id: 'D', x: 0.35, y: 0.45, frequency: 4, locked: false },
      { id: 'E', x: 0.55, y: 0.15, frequency: 3, locked: false },
      { id: 'F', x: 0.45, y: 0.45, frequency: 9, locked: false },
      { id: 'G', x: 0.65, y: 0.45, frequency: 8, locked: false },
      { id: 'H', x: 0.85, y: 0.15, frequency: 3, locked: false },
      { id: 'I', x: 0.75, y: 0.45, frequency: 2, locked: false },
      { id: 'J', x: 0.95, y: 0.45, frequency: 3, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 8 },
      { id: 'p5', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'E', target: 'G', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p7', source: 'F', target: 'G', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 8 },
      { id: 'p8', source: 'H', target: 'I', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 2 },
      { id: 'p9', source: 'H', target: 'J', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p10', source: 'I', target: 'J', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p11', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p12', source: 'B', target: 'D', type: PathwayType.Clear, harmonic: 'P5', directed: true },
    ],
  },
  constraints: { strikeBudget: 4, minimumStrikes: 3, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E', 'H'], optimal: true }],
};

// ── ch7-04 "Frequency Lock" — Tier 7, 10 nodes, 12 pw, 3 strikes
// Three disjoint clusters. Locked nodes constrain solutions.
// Cluster 1: A(6) → B(Clear+1)=5+1=6, C(Amp+2)=11+2=1, D(locked,11)
//   Solved: A=6,B=6,C=1,D=11. A-B unison:0✓ A-C P4:(6-1)=5✓ A-D P4:(6-11+12)=7→inv=5✓
//   B-D P4:(6-11+12)=7→inv=5✓
// Cluster 2: E(6) → F(Inv-1)=7-1=6, G(Clear+1)=5+1=6
//   Solved: E=6,F=6,G=6. E-F unison:0✓ E-G unison:0✓ F-G unison:0✓
// Cluster 3: H(6) → I(Damp+0)=6, J(Amp+2)=4+2=6
//   Solved: H=6,I=6,J=6. H-I unison:0✓ H-J unison:0✓ I-J unison:0✓
export const ch7_04: PuzzleDefinition = {
  id: 'ch7-04', version: 1, tier: 7,
  metadata: { title: 'Frequency Lock', category: 'constraint', author: 'resonance-team', created: '2026-03-15' },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.2, frequency: 6, locked: false },
      { id: 'B', x: 0.05, y: 0.5, frequency: 5, locked: false },
      { id: 'C', x: 0.25, y: 0.5, frequency: 11, locked: false },
      { id: 'D', x: 0.15, y: 0.8, frequency: 11, locked: true },
      { id: 'E', x: 0.5, y: 0.2, frequency: 6, locked: false },
      { id: 'F', x: 0.4, y: 0.5, frequency: 7, locked: false },
      { id: 'G', x: 0.6, y: 0.5, frequency: 5, locked: false },
      { id: 'H', x: 0.85, y: 0.2, frequency: 6, locked: false },
      { id: 'I', x: 0.75, y: 0.5, frequency: 6, locked: false },
      { id: 'J', x: 0.95, y: 0.5, frequency: 4, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'P4', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Clear, harmonic: 'P4', directed: false },
      { id: 'p4', source: 'B', target: 'D', type: PathwayType.Filtering, harmonic: 'P4', directed: false, filterThreshold: 5 },
      { id: 'p5', source: 'E', target: 'F', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'E', target: 'G', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p7', source: 'F', target: 'G', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 5 },
      { id: 'p8', source: 'H', target: 'I', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p9', source: 'H', target: 'J', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p10', source: 'I', target: 'J', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p11', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'P4', directed: true },
      { id: 'p12', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'P4', directed: false, filterThreshold: 13 },
    ],
  },
  constraints: { strikeBudget: 4, minimumStrikes: 3, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E', 'H'], optimal: true }],
};

// ── ch7-05 "Triple Inversion" — Tier 8, 10 nodes, 12 pw, 3 strikes
// Three disjoint clusters with heavy use of Inverting pathways.
// Cluster 1: A(7) → B(Inv-1)=2-1=1, C(Inv-1)=8-1=7
//   Solved: A=7,B=1,C=7. A-B P5:(7-1)=6→inv=6? No. (7-1)=6, not 7.
//   Wait. Let me recalc. A-B P4: (7-1)=6 ✗ for P4(5). Let me use m3.
//   A-B m3: (7-1)=6 ✗. Hmm. Let me change the harmonics.
//   B=2-1=1. A=7, B=1: diff=(7-1)=6, inv=(12-6)=6. Neither is 0,3,4,5,7.
//   Let me change B_start and the pathway type.
//   A(7) → B(Clear+1)=6+1=7, C(Inv-1)=8-1=7
//   Solved: A=7,B=7,C=7. All unison✓
// OK simpler. Let me make it more interesting with mixed intervals.
// Cluster 1: A(2) → B(Amp+2)=5+2=7, C(Clear+1)=1+1=2
//   Solved: A=2,B=7,C=2. A-B P4:(2-7+12)=7→inv=5✓ A-C unison:0✓ B-C P4:(7-2)=5✓
// Cluster 2: D(2) → E(Inv-1)=3-1=2, F(Amp+2)=5+2=7
//   Solved: D=2,E=2,F=7. D-E unison✓ D-F P4:(2-7+12)=7→inv=5✓ E-F P4:(2-7+12)=7→inv=5✓
// Cluster 3: G(2) → H(Clear+1)=8+1=9, I(Filter(1)+1)=1+1=2, J(locked,9)
//   Solved: G=2,H=9,I=2,J=9. G-H P5:(2-9+12)=5→inv=7✓ G-I unison:0✓ H-J unison:0✓ I-J P5:(2-9+12)=5→inv=7✓
export const ch7_05: PuzzleDefinition = {
  id: 'ch7-05', version: 1, tier: 8,
  metadata: { title: 'Triple Inversion', category: 'cascade', author: 'resonance-team', created: '2026-03-15' },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.2, frequency: 2, locked: false },
      { id: 'B', x: 0.05, y: 0.55, frequency: 5, locked: false },
      { id: 'C', x: 0.3, y: 0.55, frequency: 1, locked: false },
      { id: 'D', x: 0.5, y: 0.2, frequency: 2, locked: false },
      { id: 'E', x: 0.4, y: 0.55, frequency: 3, locked: false },
      { id: 'F', x: 0.6, y: 0.55, frequency: 5, locked: false },
      { id: 'G', x: 0.85, y: 0.2, frequency: 2, locked: false },
      { id: 'H', x: 0.7, y: 0.55, frequency: 8, locked: false },
      { id: 'I', x: 0.95, y: 0.55, frequency: 1, locked: false },
      { id: 'J', x: 0.85, y: 0.85, frequency: 9, locked: true },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'P4', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'P4', directed: false, filterThreshold: 4 },
      { id: 'p4', source: 'D', target: 'E', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'D', target: 'F', type: PathwayType.Amplifying, harmonic: 'P4', directed: false },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Filtering, harmonic: 'P4', directed: false, filterThreshold: 4 },
      { id: 'p7', source: 'G', target: 'H', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p8', source: 'G', target: 'I', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 1 },
      { id: 'p9', source: 'H', target: 'J', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p10', source: 'I', target: 'J', type: PathwayType.Clear, harmonic: 'P5', directed: true },
      { id: 'p11', source: 'G', target: 'J', type: PathwayType.Dampening, harmonic: 'P5', directed: false },
      { id: 'p12', source: 'H', target: 'I', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
    ],
  },
  constraints: { strikeBudget: 4, minimumStrikes: 3, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'D', 'G'], optimal: true }],
};

// ── ch7-06 "Prismatic Web" — Tier 8, 11 nodes, 14 pw, 4 strikes
// Four disjoint hub-spoke clusters.
// Cluster 1: A(9) → B(Clear+1)=8+1=9, C(Amp+2)=7+2=9
//   Solved: A=9,B=9,C=9. A-B unison✓ A-C unison✓ B-C unison✓
// Cluster 2: D(9) → E(Inv-1)=10-1=9, F(Damp+0)=9
//   Solved: D=9,E=9,F=9. D-E unison✓ D-F unison✓ E-F unison✓
// Cluster 3: G(9) → H(Filter(8)+1)=8+1=9
//   Solved: G=9,H=9. G-H unison✓
// Cluster 4: I(9) → J(Clear+1)=1+1=2, K(Amp+2)=12+2=2
//   Solved: I=9,J=2,K=2. I-J P5:(9-2)=7✓ I-K P5:(9-2)=7✓ J-K unison:0✓
export const ch7_06: PuzzleDefinition = {
  id: 'ch7-06', version: 1, tier: 8,
  metadata: { title: 'Prismatic Web', category: 'composition', author: 'resonance-team', created: '2026-03-15' },
  board: {
    nodes: [
      { id: 'A', x: 0.1, y: 0.2, frequency: 9, locked: false },
      { id: 'B', x: 0.05, y: 0.55, frequency: 8, locked: false },
      { id: 'C', x: 0.2, y: 0.55, frequency: 7, locked: false },
      { id: 'D', x: 0.35, y: 0.2, frequency: 9, locked: false },
      { id: 'E', x: 0.3, y: 0.55, frequency: 10, locked: false },
      { id: 'F', x: 0.45, y: 0.55, frequency: 9, locked: false },
      { id: 'G', x: 0.6, y: 0.2, frequency: 9, locked: false },
      { id: 'H', x: 0.6, y: 0.55, frequency: 8, locked: false },
      { id: 'I', x: 0.85, y: 0.2, frequency: 9, locked: false },
      { id: 'J', x: 0.75, y: 0.55, frequency: 1, locked: false },
      { id: 'K', x: 0.95, y: 0.55, frequency: 12, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 7 },
      { id: 'p4', source: 'D', target: 'E', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'D', target: 'F', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p7', source: 'G', target: 'H', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 8 },
      { id: 'p8', source: 'I', target: 'J', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p9', source: 'I', target: 'K', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p10', source: 'J', target: 'K', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 10 },
      { id: 'p11', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p12', source: 'E', target: 'F', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 8 },
      { id: 'p13', source: 'J', target: 'K', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p14', source: 'G', target: 'H', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 13 },
    ],
  },
  constraints: { strikeBudget: 5, minimumStrikes: 4, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'D', 'G', 'I'], optimal: true }],
};

// ── ch7-07 "Harmonic Labyrinth" — Tier 8, 12 nodes, 14 pw, 4 strikes
// Four disjoint clusters with mixed intervals.
// Cluster 1: A(4) → B(Clear+1)=3+1=4, C(Amp+2)=9+2=11
//   Solved: A=4,B=4,C=11. A-B unison:0✓ A-C P5:(4-11+12)=5→inv=7✓ B-C P5:(4-11+12)=5→inv=7✓
// Cluster 2: D(4) → E(Inv-1)=5-1=4, F(Clear+1)=3+1=4
//   Solved: D=4,E=4,F=4. D-E unison:0✓ D-F unison:0✓ E-F unison:0✓
// Cluster 3: G(4) → H(Amp+2)=9+2=11, I(Damp+0)=4
//   Solved: G=4,H=11,I=4. G-H P5:(4-11+12)=5→inv=7✓ G-I unison:0✓ H-I P5:(11-4)=7✓
// Cluster 4: J(4) → K(Clear+1)=10+1=11, L(locked,11)
//   Solved: J=4,K=11,L=11. J-K P5:(4-11+12)=5→inv=7✓ J-L P5:(4-11+12)=5→inv=7✓ K-L unison:0✓
export const ch7_07: PuzzleDefinition = {
  id: 'ch7-07', version: 1, tier: 8,
  metadata: { title: 'Harmonic Labyrinth', category: 'harmonic', author: 'resonance-team', created: '2026-03-15' },
  board: {
    nodes: [
      { id: 'A', x: 0.1, y: 0.15, frequency: 4, locked: false },
      { id: 'B', x: 0.05, y: 0.45, frequency: 3, locked: false },
      { id: 'C', x: 0.2, y: 0.45, frequency: 9, locked: false },
      { id: 'D', x: 0.35, y: 0.15, frequency: 4, locked: false },
      { id: 'E', x: 0.3, y: 0.45, frequency: 5, locked: false },
      { id: 'F', x: 0.45, y: 0.45, frequency: 3, locked: false },
      { id: 'G', x: 0.6, y: 0.15, frequency: 4, locked: false },
      { id: 'H', x: 0.55, y: 0.45, frequency: 9, locked: false },
      { id: 'I', x: 0.7, y: 0.45, frequency: 4, locked: false },
      { id: 'J', x: 0.85, y: 0.15, frequency: 4, locked: false },
      { id: 'K', x: 0.8, y: 0.45, frequency: 10, locked: false },
      { id: 'L', x: 0.95, y: 0.45, frequency: 11, locked: true },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 8 },
      { id: 'p4', source: 'D', target: 'E', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'D', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p7', source: 'G', target: 'H', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p8', source: 'G', target: 'I', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p9', source: 'H', target: 'I', type: PathwayType.Clear, harmonic: 'P5', directed: true },
      { id: 'p10', source: 'J', target: 'K', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p11', source: 'J', target: 'L', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p12', source: 'K', target: 'L', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 8 },
      { id: 'p13', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'P5', directed: true },
      { id: 'p14', source: 'H', target: 'I', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 8 },
    ],
  },
  constraints: { strikeBudget: 5, minimumStrikes: 4, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'D', 'G', 'J'], optimal: true }],
};

// ── ch7-08 "Dual Resonance" — Tier 8, 10 nodes, 12 pw, 3 strikes
// Three disjoint clusters using varied harmonics.
// Cluster 1: A(10) → B(Clear+1)=9+1=10, C(Inv-1)=11-1=10, D(Amp+2)=1+2=3
//   Solved: A=10,B=10,C=10,D=3. A-B unison✓ A-C unison✓ A-D P5:(10-3)=7✓ B-D P5:(10-3)=7✓
// Cluster 2: E(10) → F(Clear+1)=2+1=3, G(Amp+2)=1+2=3
//   Solved: E=10,F=3,G=3. E-F P5:(10-3)=7✓ E-G P5:(10-3)=7✓ F-G unison:0✓
// Cluster 3: H(10) → I(Damp+0)=10, J(Filter(9)+1)=9+1=10
//   Solved: H=10,I=10,J=10. H-I unison✓ H-J unison✓ I-J unison✓
export const ch7_08: PuzzleDefinition = {
  id: 'ch7-08', version: 1, tier: 8,
  metadata: { title: 'Dual Resonance', category: 'cascade', author: 'resonance-team', created: '2026-03-15' },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.2, frequency: 10, locked: false },
      { id: 'B', x: 0.05, y: 0.5, frequency: 9, locked: false },
      { id: 'C', x: 0.25, y: 0.5, frequency: 11, locked: false },
      { id: 'D', x: 0.15, y: 0.8, frequency: 1, locked: false },
      { id: 'E', x: 0.5, y: 0.2, frequency: 10, locked: false },
      { id: 'F', x: 0.4, y: 0.5, frequency: 2, locked: false },
      { id: 'G', x: 0.6, y: 0.5, frequency: 1, locked: false },
      { id: 'H', x: 0.85, y: 0.2, frequency: 10, locked: false },
      { id: 'I', x: 0.75, y: 0.5, frequency: 10, locked: false },
      { id: 'J', x: 0.95, y: 0.5, frequency: 9, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p4', source: 'B', target: 'D', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 8 },
      { id: 'p5', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'E', target: 'G', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p7', source: 'F', target: 'G', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 8 },
      { id: 'p8', source: 'H', target: 'I', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p9', source: 'H', target: 'J', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 9 },
      { id: 'p10', source: 'I', target: 'J', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p11', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'P5', directed: true },
      { id: 'p12', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: true },
    ],
  },
  constraints: { strikeBudget: 4, minimumStrikes: 3, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E', 'H'], optimal: true }],
};

// ── ch7-09 "Chromatic Descent" — Tier 9, 12 nodes, 15 pw, 4 strikes
// Four disjoint clusters, complex intervals, locked nodes.
// Cluster 1: A(1) → B(Clear+1)=12+1=1, C(Amp+2)=6+2=8
//   Solved: A=1,B=1,C=8. A-B unison:0✓ A-C P5:(1-8+12)=5→inv=7✓ B-C P5:(1-8+12)=5→inv=7✓
// Cluster 2: D(1) → E(Inv-1)=2-1=1, F(locked,8)
//   Solved: D=1,E=1,F=8. D-E unison✓ D-F P5:(1-8+12)=5→inv=7✓ E-F P5✓
// Cluster 3: G(1) → H(Amp+2)=11+2=1, I(Clear+1)=12+1=1
//   Solved: G=1,H=1,I=1. G-H unison✓ G-I unison✓ H-I unison✓
// Cluster 4: J(1) → K(Damp+0)=1, L(Filter(1)→+1)=7+1=8
//   Solved: J=1,K=1,L=8. J-K unison✓ J-L P5:(1-8+12)=5→inv=7✓ K-L P5✓
export const ch7_09: PuzzleDefinition = {
  id: 'ch7-09', version: 1, tier: 9,
  metadata: { title: 'Chromatic Descent', category: 'composition', author: 'resonance-team', created: '2026-03-15' },
  board: {
    nodes: [
      { id: 'A', x: 0.1, y: 0.15, frequency: 1, locked: false },
      { id: 'B', x: 0.05, y: 0.45, frequency: 12, locked: false },
      { id: 'C', x: 0.2, y: 0.45, frequency: 6, locked: false },
      { id: 'D', x: 0.35, y: 0.15, frequency: 1, locked: false },
      { id: 'E', x: 0.3, y: 0.45, frequency: 2, locked: false },
      { id: 'F', x: 0.45, y: 0.45, frequency: 8, locked: true },
      { id: 'G', x: 0.6, y: 0.15, frequency: 1, locked: false },
      { id: 'H', x: 0.55, y: 0.45, frequency: 11, locked: false },
      { id: 'I', x: 0.7, y: 0.45, frequency: 12, locked: false },
      { id: 'J', x: 0.85, y: 0.15, frequency: 1, locked: false },
      { id: 'K', x: 0.8, y: 0.45, frequency: 1, locked: false },
      { id: 'L', x: 0.95, y: 0.45, frequency: 7, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 10 },
      { id: 'p4', source: 'D', target: 'E', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'D', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: true },
      { id: 'p7', source: 'G', target: 'H', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p8', source: 'G', target: 'I', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p9', source: 'H', target: 'I', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 10 },
      { id: 'p10', source: 'J', target: 'K', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p11', source: 'J', target: 'L', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 1 },
      { id: 'p12', source: 'K', target: 'L', type: PathwayType.Clear, harmonic: 'P5', directed: true },
      { id: 'p13', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'P5', directed: true },
      { id: 'p14', source: 'H', target: 'I', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p15', source: 'E', target: 'F', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 10 },
    ],
  },
  constraints: { strikeBudget: 5, minimumStrikes: 4, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'D', 'G', 'J'], optimal: true }],
};

// ── ch7-10 "Grand Synthesis" — Tier 9, 12 nodes, 16 pw, 4 strikes
// The final puzzle: four disjoint clusters using every pathway type.
// Cluster 1: A(7) → B(Clear+1)=6+1=7, C(Inv-1)=8-1=7, D(Amp+2)=12+2=2
//   Solved: A=7,B=7,C=7,D=2. A-B unison✓ A-C unison✓ A-D P4:(7-2)=5✓ B-C unison✓
// Cluster 2: E(7) → F(Damp+0)=7, G(Filter(6)+1)=6+1=7
//   Solved: E=7,F=7,G=7. E-F unison✓ E-G unison✓ F-G unison✓
// Cluster 3: H(7) → I(Amp+2)=5+2=7, J(locked,2)
//   Solved: H=7,I=7,J=2. H-I unison✓ H-J P4:(7-2)=5✓ I-J P4:(7-2)=5✓
// Cluster 4: K(7) → L(Clear+1)=6+1=7
//   Solved: K=7,L=7. K-L unison✓
export const ch7_10: PuzzleDefinition = {
  id: 'ch7-10', version: 1, tier: 9,
  metadata: { title: 'Grand Synthesis', category: 'composition', author: 'resonance-team', created: '2026-03-15' },
  board: {
    nodes: [
      { id: 'A', x: 0.12, y: 0.15, frequency: 7, locked: false },
      { id: 'B', x: 0.05, y: 0.45, frequency: 6, locked: false },
      { id: 'C', x: 0.2, y: 0.45, frequency: 8, locked: false },
      { id: 'D', x: 0.12, y: 0.75, frequency: 12, locked: false },
      { id: 'E', x: 0.4, y: 0.15, frequency: 7, locked: false },
      { id: 'F', x: 0.35, y: 0.45, frequency: 7, locked: false },
      { id: 'G', x: 0.5, y: 0.45, frequency: 6, locked: false },
      { id: 'H', x: 0.7, y: 0.15, frequency: 7, locked: false },
      { id: 'I', x: 0.65, y: 0.45, frequency: 5, locked: false },
      { id: 'J', x: 0.8, y: 0.45, frequency: 2, locked: true },
      { id: 'K', x: 0.92, y: 0.15, frequency: 7, locked: false },
      { id: 'L', x: 0.92, y: 0.45, frequency: 6, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Amplifying, harmonic: 'P4', directed: false },
      { id: 'p4', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 5 },
      { id: 'p5', source: 'E', target: 'F', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'E', target: 'G', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 6 },
      { id: 'p7', source: 'F', target: 'G', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p8', source: 'H', target: 'I', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p9', source: 'H', target: 'J', type: PathwayType.Clear, harmonic: 'P4', directed: false },
      { id: 'p10', source: 'I', target: 'J', type: PathwayType.Filtering, harmonic: 'P4', directed: false, filterThreshold: 5 },
      { id: 'p11', source: 'K', target: 'L', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p12', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'P4', directed: true },
      { id: 'p13', source: 'B', target: 'D', type: PathwayType.Dampening, harmonic: 'P4', directed: false },
      { id: 'p14', source: 'F', target: 'G', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 5 },
      { id: 'p15', source: 'I', target: 'J', type: PathwayType.Clear, harmonic: 'P4', directed: true },
      { id: 'p16', source: 'K', target: 'L', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 13 },
    ],
  },
  constraints: { strikeBudget: 5, minimumStrikes: 4, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E', 'H', 'K'], optimal: true }],
};

export const chapter7Puzzles: PuzzleDefinition[] = [
  ch7_01, ch7_02, ch7_03, ch7_04, ch7_05,
  ch7_06, ch7_07, ch7_08, ch7_09, ch7_10,
];
